import { createInstance, dateReviver } from '@/api';
import { GeckoStandardTestTypeDto, GeckoSynchronisationClient, GeckoTestDto } from '@/api/backoffice/clients';
import { i18n } from '@/i18n';
import { useAlertStore } from '@/modules/core/store/alert.store';
import { defineStore } from 'pinia';
import downloadBlob from '@/helpers/download.helpers';

export interface GeckoSynchronisationStoreState {
    loading: boolean;
    reloading: boolean;
    standardTestTypes: GeckoStandardTestTypeDto[];
}

export const client = new GeckoSynchronisationClient('', createInstance(), dateReviver);

export const useGeckoSynchronisationStore = defineStore('configuration-gecko-synchronisation-store', {
    state: (): GeckoSynchronisationStoreState => ({
        standardTestTypes: [],
        loading: false,
        reloading: false
    }),
    getters: {
        getStandardTestTypeById: (state) => {
            return (id: number) => state.standardTestTypes.find((x) => x.id === id);
        }
    },
    actions: {
        async geckoExport(id: number): Promise<void> {
            try {
                const file = await client.geckoExport(id);

                return new Promise((resolve) => {
                    downloadBlob(file.data, file.fileName, () => {
                        resolve();
                    });
                });
            } catch (error) {
                useAlertStore().handleException(
                    i18n.global.t('deliveryprocessdefinition.detail.error.geckoExport.title'),
                    error
                );
            }
        },
        async geckoSync(id: number): Promise<boolean> {
            try {
                useAlertStore().clearAlert();
                await client.geckoSync(id);
                useAlertStore().setSuccess(i18n.global.t('deliveryprocessdefinition.detail.success.geckoSync.title'));

                return true;
            } catch (error) {
                useAlertStore().handleException(
                    i18n.global.t('deliveryprocessdefinition.detail.error.geckoSync.title'),
                    error,
                    false
                );
                return false;
            }
        },
        async getGeckoDefaultTests(id: number): Promise<GeckoTestDto[]> {
            try {
                const response = await client.getGeckoDefaultTests(id);
                return response;
            } catch (error) {
                return [];
            }
        },
        async getGeckoStandardTests(id: number): Promise<GeckoTestDto[]> {
            try {
                const response = await client.getGeckoStandardTests(id);
                return response;
            } catch (error) {
                return [];
            }
        },
        async getGeckoStandardTestTypes(): Promise<GeckoStandardTestTypeDto[]> {
            if (this.standardTestTypes.length > 0) {
                return this.standardTestTypes;
            }
            try {
                const response = await client.getGeckoStandardTestTypes();
                const filteredTestTypes = response?.filter((x) => x.isDisabled === false || !x.isDisabled);
                this.standardTestTypes = filteredTestTypes;
                return filteredTestTypes;
            } catch (error) {
                return [];
            }
        },
        clear() {}
    }
});
